/**
 * CLE-2811 — upload files onto a contract from its page, and have the AI read them.
 *
 * "Extract contract facts with AI after upload" (super admin only, on by default, remembered per browser)
 * sends `extractWithAi` with the upload: clear-api attaches the file and starts a contract model that
 * reads the contract's attachments. The files go up one at a time and only the last file the AI can read
 * carries the flag, sent last (`attachmentUploadPlan`), so that model reads everything she just added.
 * When one starts, a toast says so with an Open action, and the contract page's banner picks it up (the
 * upload invalidates it).
 */

import { ToastContext } from "context/ToastContext";
import {
  AI_DID_NOT_START,
  AI_DID_NOT_START_DETAIL,
  AI_NOTHING_TO_READ,
  AI_STARTED_READING,
  EXTRACT_WITH_AI_HINT,
  EXTRACT_WITH_AI_LABEL,
  OPEN_MODEL,
  UPLOAD_ATTACHMENTS_HEADER,
  UPLOAD_CANCEL,
  UPLOAD_CHOOSE_FILES,
  UPLOAD_FAILED,
  UPLOAD_NO_FILES,
  UPLOAD_REMOVE_FILE,
  UPLOAD_SUBMIT,
  uploadedFiles,
  uploadFailedFiles,
} from "pages/ContractModelDrafts/copy";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadAttachmentMutation } from "store/queries/contractFeeSchedules";

import {
  CONTRACT_ATTACHMENT_ACCEPT,
  IUploadOutcome,
  attachmentUploadPlan,
  contractModelPath,
  modelRunIdOf,
  readExtractWithAiPreference,
  summarizeUploads,
  writeExtractWithAiPreference,
} from "../contractModel";

const fileSize = (bytes: number): string => {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

interface IUploadAttachmentsDialogProps {
  contractId: string;
  visible: boolean;
  onHide: () => void;
  /** Offer "Extract contract facts with AI after upload" — super admins only. */
  canExtractWithAi: boolean;
  /** This page's URL: the contract model's back button returns here. */
  returnTo: string;
}

export function UploadAttachmentsDialog({
  contractId,
  visible,
  onHide,
  canExtractWithAi,
  returnTo,
}: IUploadAttachmentsDialogProps) {
  const toastRef = useContext(ToastContext);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [extractWithAi, setExtractWithAi] = useState<boolean>(() =>
    readExtractWithAiPreference()
  );
  const [uploading, setUploading] = useState(false);
  const [uploadAttachment] = useUploadAttachmentMutation();

  const close = () => {
    if (uploading) return;
    setFiles([]);
    onHide();
  };

  const handleUpload = async () => {
    if (files.length === 0 || uploading) return;
    const withAi = canExtractWithAi && extractWithAi;
    setUploading(true);
    // One at a time, in order: only the last upload starts the contract model (see the header).
    const plan = attachmentUploadPlan(files, withAi);
    const outcomes = await plan.reduce<Promise<IUploadOutcome[]>>(
      async (previous, step) => {
        const done = await previous;
        try {
          const result = await uploadAttachment({
            contractId,
            file: step.file,
            ...(step.extractWithAi ? { extractWithAi: true } : {}),
          }).unwrap();
          return [
            ...done,
            {
              fileName: step.file.name,
              ok: true,
              modelRunId: modelRunIdOf(result),
            },
          ];
        } catch {
          return [
            ...done,
            { fileName: step.file.name, ok: false, modelRunId: null },
          ];
        }
      },
      Promise.resolve([])
    );
    setUploading(false);

    const summary = summarizeUploads(outcomes, {
      requested: withAi,
      flagSent: plan.some((step) => step.extractWithAi),
    });
    const toast = toastRef?.current;
    if (summary.uploaded === 0) {
      toast?.show({
        severity: "error",
        summary: UPLOAD_FAILED,
        detail: uploadFailedFiles(summary.failed),
        life: 7000,
      });
      return;
    }
    toast?.show({
      severity: summary.failed.length ? "warn" : "success",
      summary: uploadedFiles(summary.uploaded),
      detail: summary.failed.length
        ? uploadFailedFiles(summary.failed)
        : undefined,
      life: 5000,
    });
    const { modelRunId } = summary;
    if (modelRunId) {
      toast?.show({
        severity: "success",
        summary: AI_STARTED_READING,
        detail: (
          <Button
            type="button"
            label={OPEN_MODEL}
            className="p-button-link p-0"
            onClick={() =>
              navigate(contractModelPath(modelRunId), {
                state: { from: returnTo },
              })
            }
          />
        ),
        life: 8000,
      });
    } else if (summary.aiNotStarted) {
      toast?.show({
        severity: "info",
        summary: AI_DID_NOT_START,
        detail: summary.aiNothingToRead
          ? AI_NOTHING_TO_READ
          : AI_DID_NOT_START_DETAIL,
        life: 7000,
      });
    }
    setFiles([]);
    onHide();
  };

  return (
    <Dialog
      header={UPLOAD_ATTACHMENTS_HEADER}
      visible={visible}
      style={{ width: "36rem", maxWidth: "95vw" }}
      onHide={close}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            label={UPLOAD_CANCEL}
            className="p-button-text"
            disabled={uploading}
            onClick={close}
          />
          <Button
            label={UPLOAD_SUBMIT}
            icon="pi pi-upload"
            loading={uploading}
            disabled={files.length === 0 || uploading}
            onClick={handleUpload}
          />
        </div>
      }
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={CONTRACT_ATTACHMENT_ACCEPT}
        data-testid="attachment-file-input"
        onChange={(e) => {
          const chosen = Array.from(e.target.files ?? []);
          if (chosen.length) setFiles((prev) => [...prev, ...chosen]);
          e.target.value = "";
        }}
      />
      <Button
        label={UPLOAD_CHOOSE_FILES}
        icon="pi pi-plus"
        className="p-button-outlined p-button-sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      />
      {files.length === 0 ? (
        <p className="text-600 text-sm mt-3 mb-0">{UPLOAD_NO_FILES}</p>
      ) : (
        <ul className="list-none p-0 mt-3 mb-0">
          {files.map((file, i) => (
            <li
              // Two files may share a name; their place in the list is what tells them apart.
              key={`${file.name}-${i}`}
              className="flex align-items-center gap-2 py-1 text-sm"
            >
              <i className="pi pi-file text-700" aria-hidden />
              <span className="flex-1" style={{ overflowWrap: "anywhere" }}>
                {file.name}
              </span>
              <span className="text-600">{fileSize(file.size)}</span>
              <Button
                type="button"
                icon="pi pi-times"
                className="p-button-text p-button-sm p-button-danger"
                aria-label={UPLOAD_REMOVE_FILE}
                disabled={uploading}
                onClick={() =>
                  setFiles((prev) => prev.filter((_, j) => j !== i))
                }
              />
            </li>
          ))}
        </ul>
      )}
      {canExtractWithAi ? (
        <div className="flex align-items-start gap-2 mt-4">
          <Checkbox
            inputId="contract-upload-extract-with-ai"
            checked={extractWithAi}
            disabled={uploading}
            onChange={(e) => {
              const next = !!e.checked;
              setExtractWithAi(next);
              writeExtractWithAiPreference(next);
            }}
          />
          <div>
            <label
              htmlFor="contract-upload-extract-with-ai"
              className="font-medium"
            >
              {EXTRACT_WITH_AI_LABEL}
            </label>
            <small className="block text-color-secondary mt-1">
              {EXTRACT_WITH_AI_HINT}
            </small>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

export default UploadAttachmentsDialog;
